import '../css/merge.css';

import { boundingExtent } from 'ol/extent'

import { saveSurface, updateLayerData } from './functions'
import { bringUserBackToStart, selectClick } from './helperFunctions'
import { removableBorders, vectorLayer as topologyLayer } from './map/layers'
import { map } from './map/tools'
import { hideEditOptions, moveTabToOperations, showToolbar } from './navigation'
import { rest } from './server'
import { minZoom, setCurrentDataExtent } from './storage'
import { token } from './tokens'

const mb = document.getElementById('mergeBtn');

const coordinateOnBoundary = (c, f) => {
  const cp = f.getGeometry().getClosestPoint(c);
  return cp[0] === c[0] && cp[1] === c[1];
};

const customGetFeaturesAtCoordinate = (c, s) => {
  const e = boundingExtent([c.map(xy => xy - .001), c.map(xy => xy + .001)]);
  return s.getFeaturesInExtent(e); // .filter(f =>
  //   f.getGeometry().intersectsExtent(e) && coordinateOnBoundary(c, f)
  // );
};

const dimensionFromType = (type) => {
  if (type.indexOf('Point') > -1) return 0;
  else if (type.indexOf('LineString') > -1) return 1;
  else if (type.indexOf('Polygon') > -1) return 2;
  else return null;
};

const oneIsBorderOfTheOther = (f1, f2) => {
  const f = [f1, f2];
  const t = f.map(fi => fi.getGeometry().getType());
  const d = t.map(ti => dimensionFromType(ti));
  const higher = d[0] > d[1] ? f[0] : f[1];
  const lower = d[0] > d[1] ? f[1] : f[0];
  const checkingCoordinates = featureFlatCoordinates(lower);
  const l = checkingCoordinates.length;
  for (let i = 0; i < l; i++) {
    const c = checkingCoordinates[i];
    if (!coordinateOnBoundary(c, higher)) return false;
  }
  return true;
};

const featureFlatCoordinates = (f) => {
  // const multi = geo.getType().indexOf('Multi') > -1;
  const coordinates = f.getGeometry().getCoordinates();
  return coordinates.flat(Infinity).reduce((result, value, i, array) => {
    if (i % 2 === 0) result.push(array.slice(i, i + 2));
    return result;
  }, []);
};

export const getAdjacentFeatures = (feature, type, s) => {
  // As of 21/05/2024, back-end provides side_faces for each removable border.
  // const id = feature.get('id'); // can be same for a polygon and a linestring
  const id = feature.ol_uid;
  const adjacent = [];
  featureFlatCoordinates(feature).forEach((c, i) => {
    // console.log(`${i + 1}. coordinate`);
    // const all = s.getFeaturesAtCoordinate(c); // faulty when c is on boudary
    const all = customGetFeaturesAtCoordinate(c, s);
    // console.log('Not filtered', all.map(f => f.getProperties()));
    const filtered = all.filter(
      f => f.ol_uid !== id && f.getGeometry().getType().indexOf(type) > -1
    );
    // console.log('Filtered', filtered.map(f => f.getProperties()));
    if (i === 0) adjacent.push(...filtered);
    else {
      // const intersection = adjacent.filter(f => filtered.includes(f));
      const union = [...new Set(adjacent.concat(filtered))];
      filtered.forEach(f => {
        if (!adjacent.includes(f)) adjacent.push(f);
      });
    }
  });
  return adjacent.filter(f => oneIsBorderOfTheOther(f, feature));
};

const coupleWithSameProperties = (couple, properties) => {
  const pl = properties.length;
  for (let i = 0; i < pl; i++) {
    const p = properties[i];
    const v = couple.map(f => f.get(p));
    if (v[0] !== v[1]) return false;
  }
  // console.log('Like egenskaper, vis knappen');
  return true;
};

const oneIsSmall = ff => {
  const aa = ff.map(f => f.getGeometry().getArea());
  return aa[0] < 500 || aa[1] < 500;
};

const pp = ['arealtype', 'treslag', 'skogbonitet', 'grunnforhold'];
export const canBeMerged = (features) => {
  // Give possibility to merge when:
  //  - only one linestring is selected and it is shared by two faces
  //  - only two polygons are selected and they share a common border
  const ff = features.getArray();
  const l = ff.length;
  const t = ff.map(f => f.getGeometry().getType());
  const dimension = t.map(type => dimensionFromType(type));
  if (l === 1 && dimension[0] === 1) {
    // console.log('Single LineString has been selected');
    mb.dataset.borderId = ff[0].get('border_ids')[0];
    return ff[0]; // we trust backend for adjacency
    // const s = topologyLayer.getSource();
    // const adjacent = getAdjacentFeatures(ff[0], 'Polygon', s);
    // // console.log('Adjacent polygons', adjacent.map(f => f.ol_uid));
    // if (adjacent.length === 2) return coupleWithSameProperties(adjacent, pp);
  } else if (l === 2 && dimension[0] === 2 && dimension[1] === 2) {
    // console.log('Polygon couple has been selected');
    const s = removableBorders.getSource();
    const adj = ff.map(f => getAdjacentFeatures(f, 'LineString', s));
    const common = adj[0].filter(f => adj[1].includes(f));
    // console.log('Shared linestrings', common.map(f => f.ol_uid));
    mb.dataset.borderId = common[0]?.get('border_ids')[0];
    // if (common.length === 1) return coupleWithSameProperties(ff, pp);
    // return coupleWithSameProperties(ff, pp) && oneIsSmall(ff);
    return common[0];
  }
  return false;
};

const requestGeometry = () => { // Reload geometry from server:
  setCurrentDataExtent([]); // force reload by skiping almostSameExtent()
  updateLayerData();
};

export const removeBorder = (id) => { // i.e. merge adjacent surfaces
  const url = `${rest}/update_surfacelayer_request${token}`;
  const p = map.getView().getProjection().getCode().split(':')[1];
  const request = new Request(url, {
    method: 'POST',
    body: JSON.stringify({ remove_line_id: id, response_srid: p }),
    headers: new Headers({ 'Content-Type': 'application/json' })
  });

  // fetch(request).then(res => res.json()).then(r => {
  fetch(request).then(r => {
    requestGeometry(); // to see new changes
    moveTabToOperations('force, without warning about missing values')
    bringUserBackToStart();
    map.getView().setMinZoom(minZoom);
    hideEditOptions();
    showToolbar('none');
  }).catch(err => { // code to handle request errors
    console.log('Back-end is not ready!', err);
  });
};

export const visibleMergeBtn = (makeVisible, commonBorder) => {
  if (makeVisible) mb.classList.remove('hidden');
  else mb.classList.add('hidden');
  mb.classList.add(commonBorder ? 'remove-border' : 'merge-surfaces');
  mb.classList.remove(!commonBorder ? 'remove-border' : 'merge-surfaces');
  editingCard.querySelector('.card-content').hidden = commonBorder ? true : false;
};

const changeAndMerge = (id) => {
  // console.log(doneButton.style.display); // gives '', not 'none'
  const d = window.getComputedStyle(doneButton).getPropertyValue('display');
  const nothingChanged = (d === 'none');
  if (nothingChanged) removeBorder(id);
  else saveSurface(selectClick.getFeatures().getArray(), id);
};

mergeBtn.onclick = (e) => changeAndMerge(e.target.dataset.borderId);
